import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  VStack,
  HStack,
  Image,
  useColorModeValue,
  Collapse,
} from '@chakra-ui/react';
import { HamburgerIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { Link } from 'react-router-dom';

const ModernNavbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [activeDropdown, setActiveDropdown] = useState(null);

  const bgColor = useColorModeValue('white', 'neutral.900');
  const borderColor = useColorModeValue('neutral.200', 'neutral.700');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    {
      label: 'หน้าหลัก',
      href: '/',
    },
    {
      label: 'เกี่ยวกับเรา',
      href: '/about-us',
      dropdown: [
        { label: 'ประวัติบ้านสิงห์ท่า', href: '/singhatha-history' },
        { label: 'ประวัติของพิพิธภัณฑ์', href: '/museum-history' },
        { label: 'เกี่ยวกับความเป็นมา', href: '/about-us' },
      ],
    },
    {
      label: 'นิทรรศการและกิจกรรม',
      href: '/exhibition-floor-1',
      dropdown: [
        { label: 'นิทรรศการชั้น 1', href: '/exhibition-floor-1' },
        { label: 'นิทรรศการชั้น 2', href: '/exhibition-floor-2' },
        { label: 'แกลเลอรี่', href: '/gallery' },
      ],
    },
    {
      label: 'ลัดเลาะรอบเมืองสิงห์ท่า',
      href: '/nearby-attractions',
    },
    {
      label: 'ติดต่อเรา',
      href: '/contact',
    },
  ];

  const DesktopMenuItem = ({ item }) => {
    const [isOpen, setIsOpen] = useState(false);

    if (item.dropdown) {
      return (
        <Box
          position="relative"
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
        >
          <Button
            variant="ghost"
            rightIcon={<ChevronDownIcon />}
            _hover={{ bg: 'neutral.50' }}
            color="neutral.900"
            fontWeight="normal"
            fontSize="md"
            px={4}
            py={2}
          >
            {item.label}
          </Button>
          <Collapse in={isOpen} animateOpacity>
            <Box
              position="absolute"
              top="100%"
              left={0}
              bg="white"
              boxShadow="lg"
              borderRadius="md"
              py={2}
              minW="200px"
              zIndex={1000}
              border="1px solid"
              borderColor="neutral.200"
            >
              {item.dropdown.map((subItem, index) => (
                <Link key={index} to={subItem.href}>
                  <Box
                    px={4}
                    py={2}
                    _hover={{ bg: 'neutral.50' }}
                    color="neutral.900"
                    fontSize="sm"
                  >
                    {subItem.label}
                  </Box>
                </Link>
              ))}
            </Box>
          </Collapse>
        </Box>
      );
    }

    return (
      <Link to={item.href}>
        <Button
          variant="ghost"
          _hover={{ bg: 'neutral.50' }}
          color="neutral.900"
          fontWeight="normal"
          fontSize="md"
          px={4}
          py={2}
        >
          {item.label}
        </Button>
      </Link>
    );
  };

  const MobileMenuItem = ({ item }) => {
    const [isOpen, setIsOpen] = useState(false);

    if (item.dropdown) {
      return (
        <Box>
          <Button
            variant="ghost"
            rightIcon={<ChevronDownIcon />}
            onClick={() => setIsOpen(!isOpen)}
            justifyContent="space-between"
            w="100%"
            color="neutral.900"
            fontWeight="normal"
            fontSize="md"
            py={4}
          >
            {item.label}
          </Button>
          <Collapse in={isOpen} animateOpacity>
            <VStack align="stretch" pl={4} spacing={0}>
              {item.dropdown.map((subItem, index) => (
                <Link key={index} to={subItem.href} onClick={onClose}>
                  <Box
                    py={3}
                    px={4}
                    color="neutral.700"
                    fontSize="sm"
                    borderBottom="1px solid"
                    borderColor="neutral.100"
                  >
                    {subItem.label}
                  </Box>
                </Link>
              ))}
            </VStack>
          </Collapse>
        </Box>
      );
    }

    return (
      <Link to={item.href} onClick={onClose}>
        <Button
          variant="ghost"
          w="100%"
          justifyContent="flex-start"
          color="neutral.900"
          fontWeight="normal"
          fontSize="md"
          py={4}
        >
          {item.label}
        </Button>
      </Link>
    );
  };

  return (
    <>
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        zIndex={1000}
        bg={bgColor}
        borderBottom="1px solid"
        borderColor={borderColor}
        transition="all 0.3s ease"
        boxShadow={isScrolled ? 'md' : 'none'}
      >
        <Flex
          maxW="1200px"
          mx="auto"
          px={4}
          py={4}
          align="center"
          justify="space-between"
        >
          {/* Logo */}
          <Link to="/">
            <Image
              src="/assets/images/logo/logo.webp"
              alt="พิพิธภัณฑ์สิงห์ท่า"
              h="50px"
              objectFit="contain"
            />
          </Link>

          {/* Desktop Menu */}
          <HStack spacing={2} display={{ base: 'none', lg: 'flex' }}>
            {menuItems.map((item, index) => (
              <DesktopMenuItem key={index} item={item} />
            ))}
          </HStack>

          {/* Mobile Menu Button */}
          <IconButton
            display={{ base: 'flex', lg: 'none' }}
            icon={<HamburgerIcon />}
            variant="ghost"
            onClick={onOpen}
            aria-label="Open menu"
            color="neutral.900"
          />
        </Flex>
      </Box>

      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <Image
              src="/assets/images/logo/logo.webp"
              alt="พิพิธภัณฑ์สิงห์ท่า"
              h="40px"
              objectFit="contain"
            />
          </DrawerHeader>
          <DrawerBody p={0}>
            <VStack spacing={0} align="stretch">
              {menuItems.map((item, index) => (
                <MobileMenuItem key={index} item={item} />
              ))}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default ModernNavbar; 